import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LayoutDashboard, Settings, Lock, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const SETTINGS_PATH = { talent: "/talent/settings", agency: "/agency/settings" };

// Standardized top-bar profile control: avatar + dropdown menu, with live avatar sync.
const HeaderProfileMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [avatar, setAvatar] = useState(user?.picture || "");
  const ref = useRef(null);

  useEffect(() => {
    if (user?.role === "talent" || user?.role === "agency") {
      api.get("/talents/me").then(({ data }) => { if (data?.profile_image_url) setAvatar(data.profile_image_url); }).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    const onAvatar = (e) => setAvatar(e.detail?.url || "");
    window.addEventListener("talentsio:avatarChanged", onAvatar);
    return () => window.removeEventListener("talentsio:avatarChanged", onAvatar);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (to) => { setOpen(false); navigate(to); };
  const doLogout = async () => { setOpen(false); await logout(); navigate("/"); };
  const initial = (user?.name || user?.email || "?")[0]?.toUpperCase();
  const settingsPath = SETTINGS_PATH[user?.role];

  const item = "w-full flex items-center gap-2.5 px-3 py-2 text-[14px] text-neutral-300 hover:text-white hover:bg-[#181818] transition-colors";

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="header-profile-btn"
        className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[#181818] transition-colors">
        {avatar ? (
          <img src={avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[#FF5C00]/15 flex items-center justify-center text-[#FF7A2F] font-semibold text-[14px]">{initial}</span>
        )}
        <ChevronDown size={15} className="text-neutral-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} data-testid="profile-menu-backdrop" />
          <div className="absolute top-full left-0 lg:left-auto lg:right-0 mt-2 w-56 min-w-[200px] rounded-xl border border-[#2A2A2A] bg-[#1A1A1A] shadow-lg overflow-hidden z-[9999]" data-testid="header-profile-menu">
            <div className="px-3 py-3 border-b border-[#2A2A2A]">
              <div className="text-[14px] text-white truncate">{user?.name || "User"}</div>
              <div className="text-[12px] text-neutral-500 truncate">{user?.email}</div>
              <div className="text-[11px] text-[#FF7A2F] capitalize mt-0.5">{user?.role}</div>
            </div>
            <div className="py-1">
              <button className={item} onClick={() => go("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard size={16} /> Dashboard</button>
              {settingsPath && <button className={item} onClick={() => go(settingsPath)} data-testid="menu-settings"><Settings size={16} /> Settings</button>}
              <button className={item} onClick={() => go("/account/privacy")} data-testid="menu-account"><Lock size={16} /> Account & privacy</button>
              <button className={`${item} text-red-400 hover:text-red-300`} onClick={doLogout} data-testid="menu-logout"><LogOut size={16} /> Sign out</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderProfileMenu;
